import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { Order } from '@/types/fulfill/inventoryHistory';

const POLLING_INTERVAL_MS = 10_000;

/**
 * Determines if an arrival order has pickups being asynchronously processed via task queue.
 * Sync creations (1 parcel) don't generate taskTracking, so no polling is needed.
 */
export const isOrderProcessingPickups = (order: Order): boolean =>
  Boolean(
    order.pickupRequired && order._id && order.pickups?.taskTracking?.status === 'processing',
  );

interface UseArrivalOrderPollingParams {
  /** Callback invoked each time a polled order returns updated data */
  onOrderUpdated: (updatedOrder: Order) => void;
}

interface UseArrivalOrderPollingReturn {
  /**
   * Register a list of orders for monitoring.
   * Call this after every successful fetch of arrival orders.
   * Only orders with taskTracking.status === 'processing' will be polled.
   */
  registerOrders: (orders: Order[]) => void;
}

/**
 * Polls arrival orders that have async pickup creation in progress.
 * Follows the same pattern as useMyReportsSection polling.
 * Applies SRP: only handles polling logic, delegates state management to the caller.
 */
const useArrivalOrderPolling = ({
  onOrderUpdated,
}: UseArrivalOrderPollingParams): UseArrivalOrderPollingReturn => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Map of _id -> last known Order for orders currently being polled.
   * Using a ref (not state) to avoid re-renders on updates.
   */
  const pollingMapRef = useRef<Map<string, Order>>(new Map());

  /** Keep the callback ref up-to-date without resetting the interval */
  const onOrderUpdatedRef = useRef(onOrderUpdated);
  useEffect(() => {
    onOrderUpdatedRef.current = onOrderUpdated;
  }, [onOrderUpdated]);

  const clearPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Guard: avoid duplicate intervals

    intervalRef.current = setInterval(async () => {
      const processingEntries = [...pollingMapRef.current.entries()];

      if (processingEntries.length === 0) {
        clearPolling();
        return;
      }

      // Fetch updated status for each processing order using ?expand=summary
      const http = initializeHttpProvider();
      const results = await Promise.allSettled(
        processingEntries.map(async ([id]) => {
          const response = await http.historyOrder.orderById(id, 'summary');
          return response.data;
        }),
      );

      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return;

        const updatedOrder = result.value;
        const [orderId, previousOrder] = processingEntries[index];

        const wasProcessing = isOrderProcessingPickups(previousOrder);
        const isStillProcessing = isOrderProcessingPickups(updatedOrder);

        if (wasProcessing && !isStillProcessing) {
          // Task completed — notify user and remove from polling map
          const taskTracking = updatedOrder.pickups?.taskTracking;
          const hasFailed =
            updatedOrder.pickups?.hasFailed || (taskTracking && taskTracking.failed > 0);

          if (hasFailed) {
            toast.error(
              `Orden ${updatedOrder.aoNumber}: Se presentaron problemas al crear ${taskTracking?.failed ?? 0} recolección(es).`,
              { autoClose: 8000 },
            );
          } else {
            toast.success(
              `Orden ${updatedOrder.aoNumber}: ¡Todas las recolecciones se crearon exitosamente!`,
              { autoClose: 6000 },
            );
          }

          pollingMapRef.current.delete(orderId);
        } else if (isStillProcessing) {
          pollingMapRef.current.set(orderId, updatedOrder);
        }

        // Notify parent to update the row in the table
        onOrderUpdatedRef.current(updatedOrder);
      });

      if (pollingMapRef.current.size === 0) {
        clearPolling();
      }
    }, POLLING_INTERVAL_MS);
  }, [clearPolling]);

  const registerOrders = useCallback(
    (orders: Order[]) => {
      // Reset polling map for the current page/filter view
      pollingMapRef.current.clear();

      const processingOrders = orders.filter(isOrderProcessingPickups);
      processingOrders.forEach((order) => {
        if (order._id) {
          pollingMapRef.current.set(order._id, order);
        }
      });

      if (processingOrders.length > 0) {
        startPolling();
      } else {
        clearPolling();
      }
    },
    [startPolling, clearPolling],
  );

  // Cleanup interval on unmount
  useEffect(() => () => clearPolling(), [clearPolling]);

  return { registerOrders };
};

export default useArrivalOrderPolling;
